import config from './Amplify';
import { FeatureFlagService } from '../Services/FeatureFlagService';
import {
    fetchAuthSession,
    getCurrentUser,
    signInWithRedirect,
    signOut as amplifySignOut,
} from 'aws-amplify/auth';
import {
    ROUTE_COLORCARD,
    ROUTE_FLOWS,
    ROUTE_JOBS,
    ROUTE_PRINTERS,
    ROUTE_SYSTEMS,
} from '../../constants';

// Only known in-app routes may be used as a post-login redirect target. An
// allowlist avoids open-redirect bypasses that string-shape checks miss (e.g.
// "/\\evil.com", which browsers normalize to a protocol-relative URL).
const ALLOWED_APP_REDIRECT_PATHS = new Set([
    ROUTE_JOBS,
    ROUTE_PRINTERS,
    ROUTE_SYSTEMS,
    ROUTE_FLOWS,
    ROUTE_COLORCARD,
]);

// Keep Amplify v6 details behind this adapter so the rest of the app does not
// depend on token/session object shapes that changed between Amplify v3 and v6.
export async function getUserIfSignedIn() {
    try {
        return await getCurrentUser();
    } catch (e) {
        return undefined;
    }
}

export async function redirectToSignIn(targetUrl) {
    const redirectTo = getAllowedAppRedirectPath(targetUrl);

    const isEntraLoginEnabled = FeatureFlagService.getInstance().getFeatureFlags().isEntraLoginEnabled === true;

    if (!redirectTo && !isEntraLoginEnabled) {
        return await signInWithRedirect();
    }

    return await signInWithRedirect({
        ...(isEntraLoginEnabled
            ? { provider: { custom: config.GMGHostedAuth.entraIdentityProviderName } }
            : {}),
        ...(redirectTo
            ? { customState: JSON.stringify({ redirectTo }) }
            : {}),
    });
}

export function getAppRedirectPathFromCustomState(customState) {
    try {
        const state = JSON.parse(customState);
        return getAllowedAppRedirectPath(state?.redirectTo);
    } catch (e) {
        return undefined;
    }
}

export function restoreAppRedirectFromCustomState(customState, location = window.location) {
    const redirectTo = getAppRedirectPathFromCustomState(customState);

    if (!redirectTo) return false;

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (currentPath !== redirectTo) {
        location.assign(redirectTo);
    }

    return true;
}

function getAllowedAppRedirectPath(path) {
    if (typeof path !== 'string') return undefined;
    if (!ALLOWED_APP_REDIRECT_PATHS.has(path)) return undefined;

    return path;
}

export async function getAccessToken() {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken;
    if (!accessToken) {
        throw new Error('Access token is unavailable: no authenticated session or access token present.');
    }
    // v6 tokens are JWT objects; existing AppSync/REST code expects raw strings.
    return accessToken.toString();
}

export async function getAccessTokenPayload() {
    const session = await fetchAuthSession();
    // getCurrentUser() no longer exposes the old v3 Cognito session object.
    // User attributes used by SessionService still live in the access-token claims.
    const payload = session.tokens?.accessToken?.payload;
    if (!payload) {
        throw new Error('Access token payload is unavailable: no authenticated session or access token present.');
    }
    return payload;
}

export async function getIdToken() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) {
        throw new Error('ID token is unavailable: no authenticated session or ID token present.');
    }
    // v6 tokens are JWT objects; existing AppSync custom headers expect strings.
    return idToken.toString();
}

export async function getAwsCredentials() {
    const session = await fetchAuthSession();
    return session.credentials;
}

// Force Cognito to fetch fresh tokens now instead of waiting for the normal refresh.
// Used by subscription recovery so the rebuilt sockets open with a valid token.
export async function refreshAuthSession() {
    return await fetchAuthSession({ forceRefresh: true });
}

export async function signOut() {
    return await amplifySignOut();
}
